// Traduce gestos táctiles / ratón y teclado a acciones abstractas:
// 'left' | 'right' | 'up' | 'down' | 'tap' | 'confirm' | 'pause' | 'mute'
// Los toques que empiezan sobre un botón de la interfaz no cuentan como gestos.

const SWIPE_MAX_TIME = 450; // ms: un gesto más lento no cuenta como deslizamiento
const TAP_MAX_TIME = 300;
const TAP_MAX_MOVE = 12; // px

const KEYMAP = {
  ArrowLeft: 'left',
  KeyA: 'left',
  ArrowRight: 'right',
  KeyD: 'right',
  ArrowUp: 'up',
  KeyW: 'up',
  ArrowDown: 'down',
  KeyS: 'down',
  Space: 'confirm',
  Enter: 'confirm',
  Escape: 'pause',
  KeyP: 'pause',
  KeyM: 'mute',
};

export class Input {
  constructor(target, onAction) {
    this.onAction = onAction;
    this.pointer = null; // { id, x, y, t, fired }

    target.addEventListener('pointerdown', (e) => this.#down(e));
    target.addEventListener('pointermove', (e) => this.#move(e), { passive: false });
    target.addEventListener('pointerup', (e) => this.#up(e));
    target.addEventListener('pointercancel', () => (this.pointer = null));
    // iOS: bloquear el zoom con pellizco y el doble toque.
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
    window.addEventListener('keydown', (e) => this.#key(e));
  }

  get threshold() {
    // Distancia mínima del deslizamiento, proporcional a la pantalla.
    return Math.max(24, Math.min(window.innerWidth, window.innerHeight) * 0.06);
  }

  #down(e) {
    if (e.target.closest?.('button')) return; // es un botón: lo gestiona la interfaz
    if (this.pointer && this.pointer.id !== e.pointerId) return; // ignora segundos dedos
    this.pointer = { id: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now(), fired: false };
  }

  #move(e) {
    const p = this.pointer;
    if (!p || p.id !== e.pointerId) return;
    e.preventDefault();
    if (p.fired) return;
    const now = performance.now();
    if (now - p.t > SWIPE_MAX_TIME) {
      // Gesto lento: reiniciamos el origen para que el siguiente movimiento rápido cuente.
      p.x = e.clientX;
      p.y = e.clientY;
      p.t = now;
      return;
    }
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    if (Math.hypot(dx, dy) < this.threshold) return;
    p.fired = true; // un solo gesto por toque, disparado en cuanto se detecta
    if (Math.abs(dx) > Math.abs(dy)) this.onAction(dx > 0 ? 'right' : 'left');
    else this.onAction(dy > 0 ? 'down' : 'up');
  }

  #up(e) {
    const p = this.pointer;
    if (!p || p.id !== e.pointerId) return;
    this.pointer = null;
    if (p.fired) return;
    const moved = Math.hypot(e.clientX - p.x, e.clientY - p.y);
    if (performance.now() - p.t < TAP_MAX_TIME && moved < TAP_MAX_MOVE) this.onAction('tap');
  }

  #key(e) {
    const action = KEYMAP[e.code];
    if (!action) return;
    e.preventDefault();
    if (e.repeat) return;
    this.onAction(action);
  }
}
